/**
 * Notification preferences rune store — M5.2, server-synced in M5.6.5.
 *
 * These are ACCOUNT-level and shared with the client portal: the same
 * `/api/me/notification-prefs` row backs both, so an operator who mutes
 * on their laptop portal is muted in the embedded widget too. That is
 * the whole point of M5.6 — before it, the mute lived in one browser's
 * `localStorage` and followed nobody anywhere.
 *
 * `localStorage` survives as a CACHE only, so the first inbound message
 * after a mount is judged against the operator's real setting instead of
 * a default that has not finished loading. The server always wins.
 *
 * Writes are optimistic: the toggle flips at once and rolls back if the
 * PUT fails. A control that waits on the network before moving reads as
 * broken, and being wrong for one chime is cheap.
 */

import { http } from '../api/http.js';

/** Pre-M5.6 key. Read once to migrate a mute, then removed. */
const LEGACY_KEY = 'widget.notificationPrefs.v1';
const CACHE_KEY = 'widget.notificationPrefs.v2';

export interface NotificationPrefsState {
  /** Default: true. Operators expect audible cues; the toggle mutes. */
  soundEnabled: boolean;
  /** Account-level master switch for Web Push. Default: true. */
  pushEnabled: boolean;
}

interface ServerPrefs extends NotificationPrefsState {
  /** `null` when the operator has never saved — see the service. */
  updatedAt: string | null;
}

const DEFAULT_STATE: NotificationPrefsState = { soundEnabled: true, pushEnabled: true };

function readStorage(key: string): NotificationPrefsState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<NotificationPrefsState> | null;
    return {
      soundEnabled:
        typeof parsed?.soundEnabled === 'boolean'
          ? parsed.soundEnabled
          : DEFAULT_STATE.soundEnabled,
      pushEnabled:
        typeof parsed?.pushEnabled === 'boolean' ? parsed.pushEnabled : DEFAULT_STATE.pushEnabled,
    };
  } catch {
    return null;
  }
}

function writeCache(state: NotificationPrefsState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(state));
  } catch {
    /* quota / privacy mode — best effort */
  }
}

function readLegacyMute(): boolean | null {
  if (typeof window === 'undefined') return null;
  let legacy: NotificationPrefsState | null = null;
  try {
    legacy = readStorage(LEGACY_KEY);
    window.localStorage.removeItem(LEGACY_KEY);
  } catch {
    return null;
  }
  if (!legacy) return null;
  // Only an explicit mute is worth carrying up. A legacy `true` cannot be
  // told apart from "never touched", and the server default is already ON.
  return legacy.soundEnabled === false ? false : null;
}

class NotificationPrefsStore {
  state = $state<NotificationPrefsState>(readStorage(CACHE_KEY) ?? { ...DEFAULT_STATE });
  /** Set after a failed write so the UI can say the setting did not stick. */
  error = $state<string | null>(null);

  private loading: Promise<void> | null = null;

  /**
   * Pull the operator's row. Safe to call from several places — concurrent
   * calls share one request. A failure is swallowed: the cached values
   * stay in play and the operator keeps working.
   */
  load(): Promise<void> {
    if (this.loading) return this.loading;
    this.loading = (async () => {
      try {
        const server = await http.get<ServerPrefs>('/api/me/notification-prefs');
        const migrated = await this.migrateLegacyMute(server);
        this.apply(migrated);
      } catch {
        // Keep the cache; nothing to tell the operator.
      } finally {
        this.loading = null;
      }
    })();
    return this.loading;
  }

  async setSoundEnabled(value: boolean): Promise<void> {
    if (this.state.soundEnabled === value) return;
    await this.write({ ...this.snapshot(), soundEnabled: value });
  }

  async setPushEnabled(value: boolean): Promise<void> {
    if (this.state.pushEnabled === value) return;
    await this.write({ ...this.snapshot(), pushEnabled: value });
  }

  async toggleSound(): Promise<void> {
    await this.setSoundEnabled(!this.state.soundEnabled);
  }

  private snapshot(): NotificationPrefsState {
    return { soundEnabled: this.state.soundEnabled, pushEnabled: this.state.pushEnabled };
  }

  /**
   * Carry a pre-M5.6 browser mute up to the account, but only when the
   * operator has no server row yet. Losing a mute the operator
   * deliberately set is the one upgrade outcome worth extra code to
   * avoid; overriding a newer choice made on another device is not.
   */
  private async migrateLegacyMute(server: ServerPrefs): Promise<ServerPrefs> {
    const legacyMute = readLegacyMute();
    if (legacyMute !== false || server.updatedAt !== null) return server;
    try {
      return await http.put<ServerPrefs>('/api/me/notification-prefs', {
        soundEnabled: false,
        pushEnabled: server.pushEnabled,
      });
    } catch {
      return server;
    }
  }

  private apply(server: ServerPrefs): void {
    this.state.soundEnabled = server.soundEnabled;
    this.state.pushEnabled = server.pushEnabled;
    writeCache(this.snapshot());
  }

  private async write(next: NotificationPrefsState): Promise<void> {
    const previous = this.snapshot();
    this.state.soundEnabled = next.soundEnabled;
    this.state.pushEnabled = next.pushEnabled;
    writeCache(next);
    this.error = null;
    try {
      const saved = await http.put<ServerPrefs>('/api/me/notification-prefs', next);
      this.apply(saved);
    } catch {
      this.state.soundEnabled = previous.soundEnabled;
      this.state.pushEnabled = previous.pushEnabled;
      writeCache(previous);
      this.error = 'Could not save. Your setting was not changed.';
    }
  }
}

export const notificationPrefs = new NotificationPrefsStore();

/**
 * Test-only — re-read the cache. Tests that stub `window.localStorage`
 * between cases need to re-hydrate the in-memory state so the next read
 * reflects the stubbed value.
 */
export function __rehydrateNotificationPrefsForTests(): void {
  const next = readStorage(CACHE_KEY) ?? { ...DEFAULT_STATE };
  notificationPrefs.state.soundEnabled = next.soundEnabled;
  notificationPrefs.state.pushEnabled = next.pushEnabled;
  notificationPrefs.error = null;
}
